'use client'

import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Loader2,
  Search,
  ShieldCheck,
  Trash2,
  UserCircle2,
  UserCheck,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { forumBadges } from '@/lib/forum-options'

type AdminUser = {
  id: number
  email: string
  handle: string | null
  avatarPath: string | null
  status: 'active' | 'suspended'
  emailVerified: boolean
  createdAt: string
  isAdmin: boolean
  isSelf: boolean
  badges: string[]
}

function formatDate(s: string) {
  const date = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function AdminUsers() {
  const [authorized, setAuthorized] = useState<boolean | null>(null)
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [editingBadgesId, setEditingBadgesId] = useState<number | null>(null)
  const [badgeDraft, setBadgeDraft] = useState<string[]>([])

  async function loadUsers() {
    const res = await fetch('/api/admin/users')
    if (!res.ok) {
      setAuthorized(false)
      return
    }
    const data = (await res.json()) as { users: AdminUser[] }
    setUsers(data.users)
    setAuthorized(true)
  }

  useEffect(() => {
    loadUsers()
      .catch(() => setAuthorized(false))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return users
    return users.filter(
      (u) => u.email.toLowerCase().includes(q) || (u.handle || '').toLowerCase().includes(q),
    )
  }, [users, query])

  async function run(path: string, successMessage: string) {
    setError('')
    setNotice('')
    const res = await fetch(path, { method: 'POST' })
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    if (!res.ok) {
      setError(data.error || 'Action failed.')
      return
    }
    setNotice(successMessage)
    await loadUsers()
  }

  async function suspend(user: AdminUser) {
    setBusyId(user.id)
    await run(`/api/admin/users/${user.id}/suspend`, `${user.email} suspended.`)
    setBusyId(null)
  }

  async function unsuspend(user: AdminUser) {
    setBusyId(user.id)
    await run(`/api/admin/users/${user.id}/unsuspend`, `${user.email} restored.`)
    setBusyId(null)
  }

  async function deleteUser(user: AdminUser) {
    if (!window.confirm(`Delete ${user.email}? This permanently removes their account and forum posts.`)) return
    setBusyId(user.id)
    setError('')
    setNotice('')
    const res = await fetch(`/api/admin/users/${user.id}`, { method: 'DELETE' })
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    if (!res.ok) {
      setError(data.error || 'Delete failed.')
      setBusyId(null)
      return
    }
    setNotice(`${user.email} deleted.`)
    await loadUsers()
    setBusyId(null)
  }

  function editBadges(user: AdminUser) {
    setEditingBadgesId((current) => (current === user.id ? null : user.id))
    setBadgeDraft(user.badges)
  }

  async function saveBadges(user: AdminUser) {
    setBusyId(user.id)
    setError('')
    setNotice('')
    try {
      const response = await fetch(`/api/admin/users/${user.id}/badges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ badges: badgeDraft }),
      })
      const data = (await response.json().catch(() => ({}))) as { error?: string }
      if (!response.ok) throw new Error(data.error || 'Could not save badges.')
      setNotice(`Badges updated for ${user.handle || user.email}.`)
      setEditingBadgesId(null)
      await loadUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save badges.')
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }

  if (authorized === false) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <ShieldCheck className="mx-auto h-8 w-8 text-destructive" />
        <h2 className="mt-4 text-lg font-semibold">Admins only</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          You need to be signed in with an administrator account to manage users.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-primary">
            <UserCircle2 className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs font-medium text-muted-foreground">Registered users</p>
            <p className="text-xl font-semibold tabular-nums">{users.length.toLocaleString()}</p>
          </div>
        </div>
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email or handle"
            className="w-full rounded-lg border border-border bg-card py-2.5 pl-9 pr-3 text-sm outline-none ring-primary/40 transition focus:border-primary/50 focus:ring-2"
          />
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2.5 text-sm text-primary">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {filtered.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
            No users match your search.
          </p>
        ) : (
          filtered.map((user) => (
            <div key={user.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                {user.avatarPath ? (
                  <img
                    src={user.avatarPath}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded-full border border-border object-cover"
                  />
                ) : (
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background text-primary">
                    <UserCircle2 className="h-6 w-6" />
                  </span>
                )}
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    <span className="truncate">{user.handle || user.email}</span>
                    {user.isAdmin && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary">
                        <ShieldCheck className="h-3 w-3" />
                        admin
                      </span>
                    )}
                    <span
                      className={`rounded-full px-2 py-0.5 font-mono text-xs ${
                        user.status === 'suspended'
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-secondary text-secondary-foreground'
                      }`}
                    >
                      {user.status}
                    </span>
                    {!user.emailVerified && (
                      <span className="rounded-full bg-accent/60 px-2 py-0.5 font-mono text-xs text-accent-foreground">
                        unverified
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 truncate text-sm text-muted-foreground">
                    {user.email} · joined {formatDate(user.createdAt)}
                  </p>
                </div>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Button
                    variant={editingBadgesId === user.id ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => editBadges(user)}
                  >
                    Badges
                    <span className="font-mono text-xs">{user.badges.length}</span>
                  </Button>
                  {user.isSelf ? (
                    <span className="text-xs text-muted-foreground">That&apos;s you</span>
                  ) : (
                    <>
                    {user.status === 'active' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busyId === user.id}
                        onClick={() => suspend(user)}
                      >
                        {busyId === user.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Ban className="h-3.5 w-3.5" />
                        )}
                        Suspend
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busyId === user.id}
                        onClick={() => unsuspend(user)}
                      >
                        {busyId === user.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <UserCheck className="h-3.5 w-3.5" />
                        )}
                        Restore
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === user.id}
                      onClick={() => deleteUser(user)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                    </>
                  )}
                </div>
              </div>

              {editingBadgesId === user.id && (
                <div className="mt-3 border-t border-border pt-3">
                  <div className="flex flex-wrap gap-2">
                    {forumBadges.map((badge) => {
                      const selected = badgeDraft.includes(badge.value)
                      return (
                        <button
                          key={badge.value}
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            setBadgeDraft((current) =>
                              selected
                                ? current.filter((item) => item !== badge.value)
                                : [...current, badge.value],
                            )
                          }
                          className={`rounded-full border px-3 py-1 text-xs ${
                            selected
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-background text-muted-foreground'
                          }`}
                        >
                          {badge.label}
                        </button>
                      )
                    })}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      size="sm"
                      disabled={busyId === user.id}
                      onClick={() => saveBadges(user)}
                    >
                      {busyId === user.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      Save badges
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
